import { getTextMatchTier } from "@/utils/text-match-tier";
import type { PickerStream } from "./picker-stream";

function searchFields(stream: PickerStream): string[] {
    return [stream.hostName, stream.name, stream.zoneName, stream.unitCategoryName].map((field) =>
        (field ?? "").trim().toLowerCase(),
    );
}

/** Lower is better: the search card's tier first, then which field matched, device first. */
export function pickerQueryRank(stream: PickerStream, query: string): number | null {
    const normalizedQuery = query.trim().toLowerCase();
    const fields = searchFields(stream);
    const ranks = fields.flatMap((field, index) => {
        const tier = getTextMatchTier(field, normalizedQuery);
        return tier === null ? [] : [tier * fields.length + index];
    });
    return ranks.length > 0 ? Math.min(...ranks) : null;
}

function toWords(text: string): string[] {
    return text
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean);
}

export function pickerQueryWordsMatch(stream: PickerStream, query: string): boolean {
    const words = searchFields(stream).flatMap(toWords);
    return toWords(query).every((queryWord) => words.some((word) => word.startsWith(queryWord)));
}

export interface PickerMatches {
    streams: PickerStream[];
    rankById: ReadonlyMap<number, number>;
}

export function withoutPlotted(catalog: readonly PickerStream[], plotted: readonly PickerStream[]): PickerStream[] {
    const plottedIds = new Set(plotted.map((stream) => stream.id));
    return catalog.filter((stream) => !plottedIds.has(stream.id));
}

/** Falls back to matching words across fields only when no field holds the whole query. */
export function filterPickerStreams(candidates: readonly PickerStream[], query: string): PickerMatches {
    if (!query.trim()) return { streams: [...candidates], rankById: new Map() };

    const streams: PickerStream[] = [];
    const rankById = new Map<number, number>();
    candidates.forEach((stream) => {
        const rank = pickerQueryRank(stream, query);
        if (rank === null) return;
        streams.push(stream);
        rankById.set(stream.id, rank);
    });
    if (streams.length > 0) return { streams, rankById };

    return { streams: candidates.filter((stream) => pickerQueryWordsMatch(stream, query)), rankById };
}
